import { type FC, Fragment } from "react";
import { Link } from "@root/shared/components/Link";
import { Icon } from "@root/shared/components/Icon";
import { LazyLoadImage } from "@root/shared/components/LazyLoadImage";
import { MultiLineText } from "@root/shared/components/MultiLineText";
import { Table } from "@root/shared/components/Table/legacy/Table";
import { DetailTableRow } from "@clientFrontend/features/publication/components/DetailTableRow";
import type { ClientContent } from "./types";
import * as styles from "./styles.css";

type Props = {
  clientContent?: ClientContent;
  canEdit: boolean;
  editUrl: string;
  dummyImgPath: string;
  site: "biyo" | "kaigo";
};

export const ClientContentDetail: FC<Props> = ({
  clientContent,
  canEdit,
  editUrl,
  dummyImgPath,
  site,
}) => {
  if (!clientContent) {
    return (
      <p>
        企業情報が設定されていません。
        <br />
        企業情報は【企業管理者】の権限をもつアカウントでのみ編集することができます。
      </p>
    );
  }

  return (
    <>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle[site]}>企業紹介</h2>
        <Table>
          <DetailTableRow header="企業画像">
            {clientContent.clientImage && (
              <figure className={styles.imageContainer}>
                <LazyLoadImage
                  src={clientContent.clientImage.src}
                  alt="企業画像"
                  dummyImgPath={dummyImgPath}
                  width={280}
                  height={158}
                />
              </figure>
            )}
          </DetailTableRow>
          <DetailTableRow header="夢・想い">
            <p className={styles.typeTitle}>{clientContent.subjectType}</p>
            <div className={styles.textByType}>
              {clientContent.dreamText && <MultiLineText text={clientContent.dreamText} />}
            </div>
          </DetailTableRow>
          <DetailTableRow header="こだわり・自慢">
            {clientContent.commitments.map((item) => (
              <Fragment key={`${item.commitmentType}_${item.commitmentText}`}>
                <p className={styles.typeTitle}>{item.commitmentType}</p>
                <div className={styles.textByType}>
                  {item.commitmentText && <MultiLineText text={item.commitmentText} />}
                </div>
              </Fragment>
            ))}
          </DetailTableRow>
          <DetailTableRow header="顔写真">
            {clientContent.ownerImage && (
              <figure className={styles.ownerImageContainer}>
                <LazyLoadImage
                  src={clientContent.ownerImage.src}
                  alt={`${clientContent.name}のメッセージ画像`}
                  dummyImgPath={dummyImgPath}
                  width={158}
                  height={158}
                />
              </figure>
            )}
          </DetailTableRow>
          <DetailTableRow header={`${clientContent.ownerType}からのメッセージ`}>
            {clientContent.ownerMessage && <MultiLineText text={clientContent.ownerMessage} />}
          </DetailTableRow>
        </Table>
      </section>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle[site]}>企業データ</h2>
        <Table>
          <DetailTableRow header="会社名">{clientContent.name}</DetailTableRow>
          <DetailTableRow header="住所">
            {`〒${clientContent.zipCode} ${clientContent.prefecture}${clientContent.city}${clientContent.address1} ${clientContent.address2}`}
          </DetailTableRow>
          <DetailTableRow header="代表者名">
            {clientContent.personName}
            {clientContent.hidePersonName && (
              <>
                <br />
                ※代表者名はサイトに表示されない設定になっております。
              </>
            )}
          </DetailTableRow>
          <DetailTableRow header="設立日">{clientContent.foundationDate}</DetailTableRow>
          <DetailTableRow header="従業員数">
            {clientContent.employeeNum && `${clientContent.employeeNum.toLocaleString()} 名`}
          </DetailTableRow>
          <DetailTableRow header="資本金">
            {clientContent.amountOfCapital &&
              `${clientContent.amountOfCapital.toLocaleString()} 円`}
          </DetailTableRow>
          <DetailTableRow header="店舗・施設数">
            {clientContent.numberOfShopsOrFacilities &&
              `${clientContent.numberOfShopsOrFacilities.toLocaleString()}  店舗・施設`}
          </DetailTableRow>
          <DetailTableRow header="役員">
            {clientContent.officer && <MultiLineText text={clientContent.officer} />}
          </DetailTableRow>
          <DetailTableRow header="事業内容">
            {clientContent.businessDetails && (
              <MultiLineText text={clientContent.businessDetails} />
            )}
          </DetailTableRow>
          <DetailTableRow header="沿革">
            {clientContent.companyHistory && <MultiLineText text={clientContent.companyHistory} />}
          </DetailTableRow>
          <DetailTableRow header="グループ会社">
            {clientContent.affiliatedCompanies && (
              <MultiLineText text={clientContent.affiliatedCompanies} />
            )}
          </DetailTableRow>
        </Table>
      </section>
      {canEdit && (
        <div className={styles.editButtonContainer}>
          <Link href={editUrl} theme="primary" className={styles.editButton}>
            <Icon name="edit" />
            編集する
          </Link>
        </div>
      )}
    </>
  );
};
